import { create } from "zustand";
import { useEffect } from "react";
import type Keycloak from "keycloak-js";
import KeycloakService from "@/lib/services/keycloak";

interface AuthStore {
  keycloak: Keycloak;
  initialized: boolean;
  keycloakReady: boolean;
  isAuthenticated: boolean;
  username?: string;
  login: () => void;
  logout: () => void;
  getToken: () => string | undefined;
  updateToken: () => Promise<void>;
  initialize: () => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  keycloak: KeycloakService.keycloak,
  initialized: false,
  keycloakReady: false,
  isAuthenticated: false,
  username: undefined,

  login: () => KeycloakService.doLogin(),
  logout: () => KeycloakService.doLogout(),
  getToken: () => KeycloakService.getToken(),
  updateToken: async () => {
    return new Promise((resolve) => {
      KeycloakService.updateToken(() => {
        set({
          isAuthenticated: KeycloakService.isAuthenticated(),
          username: KeycloakService.getUsername(),
        });
        resolve();
      });
    });
  },
  initialize: () => {
    KeycloakService.initKeycloak(() => {
      set({
        initialized: true,
        keycloakReady: true,
        isAuthenticated: KeycloakService.isAuthenticated(),
        username: KeycloakService.getUsername(),
      });
    });
  },
}));

// Hook to listen for Keycloak events and update Zustand store
export const useAuthListener = () => {
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    initialize();

    const authSuccessHandler = () => {
      console.debug("Auth success event in Zustand store");
      useAuthStore.setState({
        isAuthenticated: KeycloakService.isAuthenticated(),
        username: KeycloakService.getUsername(),
      });
    };

    const authLogoutHandler = () => {
      console.debug("Auth logout event in Zustand store");
      useAuthStore.setState({
        isAuthenticated: false,
        username: undefined,
      });
    };

    KeycloakService.addEventListener("onAuthSuccess", authSuccessHandler);
    KeycloakService.addEventListener("onAuthLogout", authLogoutHandler);

    return () => {
      KeycloakService.removeEventListener("onAuthSuccess", authSuccessHandler);
      KeycloakService.removeEventListener("onAuthLogout", authLogoutHandler);
    };
  }, [initialize]);
};
